import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import ProjectRoutes from '@Pages/RetroSynthesis/ProjectRoutes/ProjectRoutes';
import { withRouter } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { REDUCER_KEY_RS } from '@Pages/RetroSynthesis/RetroSynthesisProjects/constants';
import { REDUCER_KEY_PROJECT_ROUTES } from '@Pages/RetroSynthesis/ProjectRoutes/constants';
import { openWith3d as openStructureDialogAction } from '@Containers/StructureDialog/StructureDialogAction';
import { getUrlParamByName, getUrlParams, getUrlParamsArrayByName } from '@Pages/RetroSynthesis/utils';
import { RouteType, UrlParams } from '@Pages/RetroSynthesis/constants';
import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';

import { getProjectsInfoMap } from '@Pages/RetroSynthesis/RsBreadcrumbs/selector';
import { fetchProjectBreadcrumbInfoAction } from '@Pages/RetroSynthesis/RsBreadcrumbs/actions';
import get from 'lodash/get';
import getSynthesisMenuOptions from '@Pages/RetroSynthesis/StructureOption/selectors';
import { stringify } from 'query-string';
import { deleteProjectsAction } from '@Pages/RetroSynthesis/RetroSynthesisProjects/actions';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import { onRoutesChanged, onRoutesLoaded } from '@Pages/RetroSynthesis/ProjectRoutes/analytics';
import {
  bulkDeleteProjectRoutesAction,
  fetchProjectRoutesAction,
  fetchProjectStructureAction,
  projectRouteClearStateAction,
  projectRoutesSelectionChanged,
  setViewRoutesPositionAction,
} from './actions';
import {
  isIktosPredictiveRetrosynthesisEnabled,
  isPaiPredictiveRetrosynthesisEnabled,
  isPredictiveRetrosynthesisEnabled,
} from '../predictiveUtils';
/* eslint-disable no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
function ProjectRoutesContainer({
  isLoading,
  response,
  selectedIds,
  setSelectedIds,
  match,
  history,
  location,
  targetResponse,
  fetchProjectRoutes,
  fetchProjectStructure,
  openStructureDialog,
  clearState,
  synthesisMenuOptions,
  projectsInfo,
  fetchProjectInfo,
  bulkDeleteRoutes,
  remainingRoutesCount,
  deleteProjects,
  lastViewedRoutesPosition,
  setViewRoutesPosition,
}) {
  const { totalElements } = response || {};
  const { projectId } = (match && match.params) || {};
  const projectInfo = projectsInfo && projectsInfo[projectId];
  const publishedRoutesCount = get(projectInfo, 'publishedRoutesCount', 0);
  const predictedRoutesCount = get(projectInfo, 'predictedRoutesCount', 0);
  const customizedRoutesCount = get(projectInfo, 'customizedRoutesCount', 0);
  const combinedRoutesCount = get(projectInfo, 'combinedRoutesCount', 0);

  if (isEmpty(projectId)) {
    return 'ProjectId not specified';
  }

  // Re-fetch projects info if number of routes changes
  useEffect(() => {
    if (
      isNumber(totalElements) &&
      publishedRoutesCount + predictedRoutesCount + customizedRoutesCount + (combinedRoutesCount || 0) !== totalElements
    ) {
      fetchProjectInfo(projectId);
    }
  }, [totalElements]);

  useEffect(() => {
    fetchProjectStructure(projectId);
  }, [projectId]);

  const fetchData = (location) => {
    const params = {
      [UrlParams.IDS]: getUrlParamByName(location, UrlParams.LIMIT_TO),
      [UrlParams.PAGE]: getUrlParamByName(location, UrlParams.PAGE),
      [UrlParams.SIZE]: getUrlParamByName(location, UrlParams.SIZE),
      [UrlParams.SORT]: getUrlParamByName(location, UrlParams.SORT),
    };
    fetchProjectRoutes(projectId, params, history);
  };

  useEffect(() => {
    setSelectedIds([]);
    fetchData(location);
    return clearState; // Clean redux state in case component unmounted
  }, []);

  useEffect(() => {
    if (response && response.content) {
      // track routes loading
      const params = {
        [UrlParams.IDS]: getUrlParamByName(location, UrlParams.LIMIT_TO),
        [UrlParams.PAGE]: getUrlParamByName(location, UrlParams.PAGE),
        [UrlParams.SIZE]: getUrlParamByName(location, UrlParams.SIZE),
        [UrlParams.SORT]: getUrlParamByName(location, UrlParams.SORT),
      };
      if (every(params, isEmpty)) {
        onRoutesLoaded(projectId, response.content, projectInfo);
      } else {
        onRoutesChanged(projectId, response.content, projectInfo);
      }
    }
  }, [response]);

  useEffect(() => history.listen((location) => fetchData(location)), [history]);

  useEffect(() => {
    (async () => {
      if (remainingRoutesCount == null) return;

      if (remainingRoutesCount === 0) {
        await deleteProjects([projectId]);
        history.replace(RsRoute.PROJECTS_LIST);
        return;
      }

      const limited = getUrlParamsArrayByName(location, UrlParams.LIMIT_TO);
      if (limited) {
        // delete routes with limit selected
        if (remainingRoutesCount > 0 && limited.length && selectedIds.length < limited.length) {
          // partial delete of limited routes
          const remaining = limited.filter((p) => !selectedIds.includes(parseInt(p, 10)));
          const newParams = getUrlParams(location);
          if (remaining.length) newParams[UrlParams.LIMIT_TO] = remaining;
          history.replace({ ...location, search: stringify(newParams, { encode: false }) });
        } else {
          // delete all limited routes - navigate to all routes
          history.replace(RsRoute.getProjectLink(projectId));
        }
      } else {
        // simple delete some routes without limit
        fetchData(location);
      }
      fetchProjectInfo(projectId);
      setSelectedIds([]);
    })();
  }, [remainingRoutesCount]);

  const projectStructure = targetResponse && targetResponse.structureDto && targetResponse.structureDto.structure;
  const projectName = targetResponse && targetResponse.projectName;
  const isConfidenceColumnShown = isPredictiveRetrosynthesisEnabled() && (predictedRoutesCount > 0 || combinedRoutesCount > 0);
  const isFirstDisconnectionColumnShown = isIktosPredictiveRetrosynthesisEnabled() && (predictedRoutesCount > 0 || combinedRoutesCount > 0);
  const isPaiUser = isPaiPredictiveRetrosynthesisEnabled();
  const routesCount = {
    [RouteType.PUBLISHED]: (projectInfo && projectInfo.publishedRoutesCount) || 0,
    [RouteType.PREDICTED]: (projectInfo && projectInfo.predictedRoutesCount) || 0,
    [RouteType.CUSTOMIZED]: (projectInfo && projectInfo.customizedRoutesCount) || 0,
    [RouteType.COMBINED]: (projectInfo && projectInfo.combinedRoutesCount) || 0,
  };

  const doChanges = useCallback(
    (projectId, params) => {
      const oldParams = getUrlParams(location);
      const newParams = {
        [UrlParams.LIMIT_TO]: getUrlParamsArrayByName(location, UrlParams.LIMIT_TO),
        ...params,
      };
      if (!isEqual(oldParams, newParams)) {
        history.push({
          ...location,
          search: stringify(newParams, { encode: false }),
        });
        setSelectedIds([]);
      }
    },
    [history, location]
  );

  return (
    <ProjectRoutes
      projectId={projectId}
      response={response}
      isLoading={isLoading}
      selectedIds={selectedIds}
      doChanges={doChanges}
      setSelectedIds={setSelectedIds}
      projectStructure={projectStructure}
      synthesisMenuOptions={synthesisMenuOptions}
      doZoomStructure={() => openStructureDialog(null, projectStructure, projectName)}
      isConfidenceColumnShown={isConfidenceColumnShown}
      isFirstDisconnectionColumnShown={isFirstDisconnectionColumnShown}
      doDeleteRoutes={bulkDeleteRoutes}
      routesCount={routesCount}
      projectInfo={projectInfo}
      lastViewedRoutesPosition={lastViewedRoutesPosition}
      setViewRoutesPosition={setViewRoutesPosition}
      isPaiUser={isPaiUser}
    />
  );
}

const mapStateToProps = (state) => {
  const { response, isLoading, selectedIds, targetResponse, remainingRoutesCount, lastViewedRoutesPosition } = state
    .get(REDUCER_KEY_RS)
    .get(REDUCER_KEY_PROJECT_ROUTES);
  return {
    response,
    isLoading,
    selectedIds,
    targetResponse,
    synthesisMenuOptions: getSynthesisMenuOptions(state),
    projectsInfo: getProjectsInfoMap(state),
    remainingRoutesCount,
    lastViewedRoutesPosition,
  };
};

const mapActionToProps = {
  fetchProjectRoutes: fetchProjectRoutesAction,
  setSelectedIds: projectRoutesSelectionChanged,
  fetchProjectStructure: fetchProjectStructureAction,
  openStructureDialog: openStructureDialogAction,
  clearState: projectRouteClearStateAction,
  fetchProjectInfo: fetchProjectBreadcrumbInfoAction,
  bulkDeleteRoutes: bulkDeleteProjectRoutesAction,
  deleteProjects: deleteProjectsAction,
  setViewRoutesPosition: setViewRoutesPositionAction,
};

export default connect(mapStateToProps, mapActionToProps)(withRouter(ProjectRoutesContainer));
